import { Children, isValidElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon.js';
import Swipeable, { type SwipeAction } from './Swipeable.js';

/**
 * iOS inset grouped list: what he *browses* (tasks, messages, More,
 * settings, details). Hairline separators inset past the leading slot.
 * A group with no rows draws nothing at all — no empty header (the Smart
 * Stack rule, spec §1.3).
 */
export function Group({
  header,
  footer,
  action,
  id,
  className,
  label,
  children,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  /** A small link/button on the header's right ("Edit", "See all"). */
  action?: { label: string; onClick?: () => void; to?: string };
  id?: string;
  className?: string;
  /** aria-label for the section when the header is not plain text. */
  label?: string;
  children?: ReactNode;
}): JSX.Element | null {
  // toArray already drops null, undefined and booleans.
  const rows = Children.toArray(children);
  if (rows.length === 0) return null;
  const headId = id ? `${id}-head` : undefined;
  return (
    <section className={`ui-groupwrap${className ? ` ${className}` : ''}`} aria-labelledby={header && headId ? headId : undefined} aria-label={label}>
      {(header || action) && (
        <div className="ui-group__head">
          {header && (
            <h2 className="ui-group__title" id={headId}>
              {header}
            </h2>
          )}
          {action &&
            (action.to ? (
              <Link className="ui-group__action" to={action.to}>
                {action.label}
              </Link>
            ) : (
              <button type="button" className="ui-group__action" onClick={action.onClick}>
                {action.label}
              </button>
            ))}
        </div>
      )}
      <ul className="ui-group">{rows}</ul>
      {footer && <p className="ui-group__foot">{footer}</p>}
    </section>
  );
}

export interface RowProps {
  title: ReactNode;
  sub?: ReactNode;
  /** Icon tile, dot, checkbox… (36px slot). */
  leading?: ReactNode;
  /** Secondary value on the right, before the chevron ("$98", "in 2d"). */
  detail?: ReactNode;
  /** Controls on the right (buttons); never inside the tap target. */
  trailing?: ReactNode;
  to?: string;
  onClick?: () => void;
  chevron?: boolean;
  tone?: 'default' | 'muted' | 'bad' | 'accent';
  swipe?: { leading?: SwipeAction; trailing?: readonly SwipeAction[] };
  onLongPress?: () => void;
  /** Sphere colour: a 3px bar on the row's leading edge. */
  bar?: string;
  className?: string;
  ariaLabel?: string;
}

/** One list row (≥ 48px). The title area is the tap target when `to`/`onClick` is set. */
export function Row({
  title,
  sub,
  leading,
  detail,
  trailing,
  to,
  onClick,
  chevron = Boolean(to),
  tone = 'default',
  swipe,
  onLongPress,
  bar,
  className,
  ariaLabel,
}: RowProps): JSX.Element {
  const body = (
    <>
      <span className="ui-row__text">
        <span className="ui-row__title">{title}</span>
        {sub && <span className="ui-row__sub">{sub}</span>}
      </span>
      {detail !== undefined && detail !== null && detail !== false && <span className="ui-row__detail">{detail}</span>}
      {chevron && <Icon name="chevron-right" size={18} className="ui-row__chev" />}
    </>
  );
  const main = to ? (
    <Link to={to} className="ui-row__main" aria-label={ariaLabel}>
      {body}
    </Link>
  ) : onClick ? (
    <button type="button" className="ui-row__main" onClick={onClick} aria-label={ariaLabel}>
      {body}
    </button>
  ) : (
    <div className="ui-row__main">{body}</div>
  );
  const inner = (
    <div className={`ui-row ui-row--${tone}${to || onClick ? ' ui-row--tap' : ''}`}>
      {bar && <span className="ui-row__bar" style={{ background: bar }} aria-hidden="true" />}
      {leading && <span className="ui-row__lead">{leading}</span>}
      {main}
      {trailing && <span className="ui-row__trail">{trailing}</span>}
    </div>
  );
  return (
    <Swipeable leading={swipe?.leading} trailing={swipe?.trailing} onLongPress={onLongPress} className={className}>
      {inner}
    </Swipeable>
  );
}

/** A rounded-square icon tile for a row's leading slot (Settings style). */
export function IconTile({ name, color }: { name: Parameters<typeof Icon>[0]['name']; color: string }): JSX.Element {
  return (
    <span className="ui-tile" style={{ background: color }} aria-hidden="true">
      <Icon name={name} size={18} />
    </span>
  );
}

/** "Show N more" inside a Group (keeps the list short by default). */
export function MoreRow({ count, noun = 'more', onClick }: { count: number; noun?: string; onClick: () => void }): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <li className="ui-swipe">
      <button type="button" className="ui-more" onClick={onClick}>
        Show {count} {noun}
      </button>
    </li>
  );
}

/** True when `node` would render a row (used by pages composing optional rows). */
export function hasRows(node: ReactNode): boolean {
  return Children.toArray(node).some((c) => isValidElement(c));
}
